import React, { useState } from 'react'
import { LockOpenOutlined } from '@mui/icons-material'
import { apiGet, apiPut } from '../generic/Api_Functions'
import AlertUI from '../generic/AlertUI'
import PasswordDialog from '../generic/password/PasswordDialog'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'

// Endpoint proposed to sun (sun-contract-proposal.md section 8): a device that went
// NON_OPERATIVE needs a manual action to return to its proper operating mode
// (AUTO/MANUAL — sun decides). No such endpoint exists yet; jupiter calls the
// proposed route and degrades to an alert if it isn't there.
const UNLOCK_ENDPOINT = 'device/unlock/'
const REQUIRE_PASSWORD_SETTING = 'settings/REQUIRE_PASSWORD'

/**
 * AssetUnlockButton — quick access from the card to clear a machine's NON_OPERATIVE
 * mode (PO): a machine in NON_OPERATIVE stays locked until someone manually returns
 * it to its operating mode. Renders NOTHING unless `device.mode === 'NON_OPERATIVE'`.
 *
 * By default it asks for the admin password before unlocking, gated by the same
 * `REQUIRE_PASSWORD` setting the cell start-up flow uses (CheckDialog.jsx), and
 * reusing the same `PasswordDialog`. On success it calls the proposed unlock
 * endpoint; the screen's periodic snapshot tick then reflects the new mode.
 *
 * Self-contained (own AlertUI + PasswordDialog); `stopPropagation` keeps a click
 * from also triggering the card header's open-detail.
 */
export default function AssetUnlockButton ({ device, onUnlocked = () => {} }) {
  // Thin gate with NO hooks so an operational device pulls in nothing (AlertUI —
  // and PasswordDialog — depend on react-router; keeping them out of the
  // always-rendered path avoids leaking a <Router> requirement into every card).
  if (device?.mode !== 'NON_OPERATIVE') return null
  return <UnlockControl device={device} onUnlocked={onUnlocked} />
}

function UnlockControl ({ device, onUnlocked }) {
  const { t_ } = useSmartTranslation()
  const [AlertElement, showAlert] = AlertUI()
  const [passwordOpen, setPasswordOpen] = useState(false)

  const unlock = async () => {
    try {
      await apiPut(UNLOCK_ENDPOINT + device.id)
      showAlert({ title: t_('Machine unlocked'), severity: 'success', message: '' })
      onUnlocked()
    } catch (error) {
      showAlert({ title: t_('Could not unlock the machine'), severity: error.name ?? 'error', message: t_(error.message) })
    }
  }

  const onTriggerClick = async (event) => {
    event.stopPropagation()
    // Same gate as the cell start-up flow (CheckDialog): default to requiring the
    // password when the setting cannot be read.
    let requirePassword = true
    try {
      const setting = await apiGet(REQUIRE_PASSWORD_SETTING)
      requirePassword = setting?.value === 'true'
    } catch (error) {
      requirePassword = true
    }
    if (requirePassword) setPasswordOpen(true)
    else unlock()
  }

  return (
    <>
      {AlertElement}
      <button
        type='button'
        data-testid={`asset-unlock-${device.id}`}
        className='mcx-unlock-btn'
        onClick={onTriggerClick}
      >
        <LockOpenOutlined sx={{ fontSize: 15 }} />
        <span>{t_('Unlock')}</span>
      </button>
      <PasswordDialog
        open={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        onAccept={() => { setPasswordOpen(false); unlock() }}
      />
    </>
  )
}
